import { DropdownSkeleton, FilterableMultiSelect } from "@carbon/react";
import { FilterableMultiSelectProps } from "@carbon/react/lib/components/MultiSelect/FilterableMultiSelect";
import { ServerError } from "@flaminem/utils";

export type FilterableMultiPickerProps<TItem> = {
  loading?: boolean;
  error?: ServerError | null;
  onChange: (changes: { selectedItems: TItem[] }) => void;
  renderItem?: (item: TItem) => React.ReactNode;
} & Pick<
  FilterableMultiSelectProps<TItem>,
  "items" | "helperText" | "titleText" | "invalidText" | "itemToString" | "id" | "selectedItems"
>;

function FilterableMultiPicker<TItem>({
  items,
  titleText,
  invalidText,
  loading,
  error,
  helperText,
  itemToString,
  onChange,
  id,
  selectedItems,
  renderItem,
}: FilterableMultiPickerProps<TItem>) {
  if (loading) return <DropdownSkeleton />;

  const filterItems = (
    ...args: Parameters<NonNullable<FilterableMultiSelectProps<TItem>["filterItems"]>>
  ) => {
    const [items, { itemToString, inputValue }] = args;
    const lowerCaseInputValue = inputValue?.toLowerCase() ?? "";

    return items.filter((item) => {
      const itemStr = itemToString ? itemToString(item) : item?.toString() ?? "";
      return itemStr.toLowerCase().includes(lowerCaseInputValue);
    });
  };

  return (
    <FilterableMultiSelect
      className="w-full"
      id={id}
      itemToString={itemToString}
      items={items}
      titleText={titleText}
      invalid={!!error}
      invalidText={error?.message || invalidText}
      helperText={helperText}
      filterItems={filterItems}
      onChange={onChange}
      selectedItems={selectedItems}
      itemToElement={renderItem}
    />
  );
}

export default FilterableMultiPicker;
